import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";
import { useLoyaltySettings } from "../../hooks/useLoyaltySettings";
import { useLoyaltyTiersUiCatalog } from "../../hooks/useLoyaltyTiersUiCatalog";
import { validateTierPurchaseThresholds } from "../../utils/loyaltyQualification";
import TierThresholdFields from "./TierThresholdFields";

const INITIAL_FORM = { key: "", name: "", min_status_points: 0, min_purchase_count: 0, min_purchase_amount: 0, rank: 0, active: true };

const CreateTierDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const { qualificationMode } = useLoyaltySettings(brandName);
    const { catalog } = useLoyaltyTiersUiCatalog(brandName);
    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setForm((p) => ({ ...p, [field]: value }));
    };

    const isValid = !!(brandName && form.name?.trim());

    const handleSubmit = async () => {
        if (!isValid) return;
        const thresholdCheck = validateTierPurchaseThresholds(form, qualificationMode, { isBase: false });
        if (!thresholdCheck.valid) {
            setError(thresholdCheck.error);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify({
                    brand: brandName,
                    name: form.name.trim(),
                    min_status_points: form.min_status_points,
                    min_purchase_count: form.min_purchase_count,
                    min_purchase_amount: form.min_purchase_amount,
                    rank: form.rank,
                    active: form.active,
                }),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setError(null); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.tiers.gradient }}>
                        <EmojiEventsIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={600} color="#2c3e50">
                        Nouveau Niveau de Fidélité
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom du niveau" value={form.name}
                            onChange={handleChange("name")} placeholder="ex: Gold" size="small"
                            disabled={!brandName} helperText="Nom affiché aux utilisateurs"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <TierThresholdFields
                        form={form}
                        onChange={handleChange}
                        qualificationMode={qualificationMode}
                        disabled={!brandName}
                        catalogHints={catalog?.create || catalog?.fields || {}}
                    />
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between"
                            px={1.5} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer ce niveau</Typography>
                                <Typography fontSize={11} color="text.secondary">Le niveau sera immédiatement disponible pour les clients</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.tiers.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.tiers.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.tiers.gradient, color: "#fff", textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.tiers.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer le Niveau"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTierDialog;
